import { Option } from '@sapphire/result';

import {
  FeasibilityCriterion,
  FeasibilityResult,
  FeasibilitySupplierBreakdown,
} from '@ps/survey-utils';

/**
 * Union of the unused condition ids per qualification code, across every entry
 * the response sent. One place — the constructor used to repeat this over the
 * already-merged record it was handed.
 */
function mergeUnusedCriteria(
  entries: readonly Record<string, readonly string[]>[],
): Record<string, string[]> {
  const merged: Record<string, string[]> = {};

  entries.forEach((entry) => {
    Object.entries(entry).forEach(([qualificationCode, ids]) => {
      merged[qualificationCode] = [
        ...new Set([...(merged[qualificationCode] ?? []), ...ids]),
      ];
    });
  });

  return merged;
}

/**
 * Analysis of FeasibilityResult: the total, a quota's feasible count,
 * whether it's difficult, and whether a qualification/criterion was
 * excluded from the calculation (UNKN).
 */
export class FeasibilityAnalysis {
  private constructor(
    private readonly _feasibility: FeasibilityResult,
    private readonly _unusedCriteria: Record<string, string[]>,
  ) {}

  public static from(feasibility: FeasibilityResult): FeasibilityAnalysis {
    return new FeasibilityAnalysis(
      feasibility,
      mergeUnusedCriteria(feasibility.unusedCriteria),
    );
  }

  public get total(): number {
    return this._feasibility.totalFeasibility;
  }

  public get currencySymbol(): string {
    return this._feasibility.currency.symbol;
  }

  public get feasibilityCpi(): number {
    return this._feasibility.feasibilityCpi;
  }

  public get suppliers(): readonly FeasibilitySupplierBreakdown[] {
    return this._feasibility.suppliers;
  }

  public count(buyerQuotaId: string): number {
    return Option.from(
      this._feasibility.quotas.find(
        (quota) => quota.buyerQuotaId === buyerQuotaId,
      ),
    ).mapOr(0, (quota) => quota.count);
  }

  public isDifficult(buyerQuotaId: string): boolean {
    return this._feasibility.difficultQuotaIds.includes(buyerQuotaId);
  }

  public hasPopulationData(qualificationCode: number): boolean {
    return !this._feasibility.unusedQualifications.includes(qualificationCode);
  }

  public isUnknown(
    qualificationCode: number,
    criterion: FeasibilityCriterion,
  ): boolean {
    return (
      this._feasibility.unusedQualifications.includes(qualificationCode) ||
      this._hasUnusedCondition(qualificationCode, criterion)
    );
  }

  /** Deduplicated, upper-cased qualification names — feeds the warning toast copy. */
  public unusedQualificationNames(
    criteria: readonly FeasibilityCriterion[],
  ): string[] {
    const names = new Set<string>();

    criteria.forEach((criterion) => {
      if (
        criterion.qualificationName &&
        this.isUnknown(criterion.qualificationCode, criterion)
      ) {
        names.add(criterion.qualificationName.toUpperCase());
      }
    });

    return [...names];
  }

  private _hasUnusedCondition(
    qualificationCode: number,
    { conditionCodes = [], rangeSets = [] }: FeasibilityCriterion,
  ): boolean {
    const unused = this._unusedCriteria[qualificationCode];
    if (!unused) {
      return false;
    }

    if (conditionCodes.length) {
      return conditionCodes.some((code) => unused.includes(code));
    }

    if (rangeSets.length) {
      return rangeSets.some(({ from, to }) => unused.includes(`${from}-${to}`));
    }

    return false;
  }
}
